import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const BeerStyleGuide = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant' // or 'smooth' for smooth scrolling
    });
  }, []);

  const getBeerData = useMemo(() => [
    {
      category: t('beerGuide.categories.lagers'),
      styles: [
        { key: 'pilsner', abv: "4.4-5.2%", ibu: "25-45", srm: "2-5" },
        { key: 'czech', abv: "4.2-5.8%", ibu: "30-45", srm: "3-6" },
        { key: 'helles', abv: "4.7-5.4%", ibu: "18-25", srm: "3-5" },
        { key: 'marzen', abv: "5.8-6.3%", ibu: "18-24", srm: "8-17" },
        { key: 'vienna', abv: "4.7-5.5%", ibu: "18-30", srm: "9-15" }
      ]
    },
    {
      category: t('beerGuide.categories.ipas'),
      styles: [
        { key: 'apa', abv: "4.5-6.2%", ibu: "30-50", srm: "5-10" },
        { key: 'aipa', abv: "5.5-7.5%", ibu: "40-70", srm: "6-14" },
        { key: 'neipa', abv: "6.0-9.0%", ibu: "25-60", srm: "3-7" },
        { key: 'dipa', abv: "7.5-10.0%", ibu: "60-120", srm: "6-14" },
        { key: 'engipa', abv: "5.0-7.5%", ibu: "40-60", srm: "6-14" }
      ]
    },
    {
      category: t('beerGuide.categories.stouts'),
      styles: [
        { key: 'stout', abv: "4.0-4.5%", ibu: "25-45", srm: "25-40" },
        { key: 'astout', abv: "5.0-7.0%", ibu: "35-75", srm: "30-40+" },
        { key: 'istout', abv: "8.0-12.0%", ibu: "50-90", srm: "30-40+" },
        { key: 'baltic', abv: "6.5-9.5%", ibu: "20-40", srm: "17-30" }
      ]
    },
    {
      category: t('beerGuide.categories.belgian'),
      styles: [
        { key: 'weiss', abv: "4.3-5.6%", ibu: "8-15", srm: "2-6" },
        { key: 'wit', abv: "4.5-5.5%", ibu: "8-20", srm: "2-4" },
        { key: 'dubbel', abv: "6.0-7.6%", ibu: "15-25", srm: "10-17" },
        { key: 'tripel', abv: "7.5-9.5%", ibu: "20-40", srm: "4-7" },
        { key: 'saison', abv: "5.0-7.0%", ibu: "20-35", srm: "5-14" }
      ]
    },
    {
      category: t('beerGuide.categories.sour'),
      styles: [
        { key: 'berliner', abv: "2.8-3.8%", ibu: "3-8", srm: "2-3" },
        { key: 'gose', abv: "4.2-4.8%", ibu: "5-12", srm: "2-4" },
        { key: 'lambic', abv: "5.0-8.0%", ibu: "0-10", srm: "3-7" },
        { key: 'flanders', abv: "4.6-6.5%", ibu: "10-25", srm: "10-16" }
      ]
    }
  ], [t]);

  const filteredData = useMemo(() => {
    return getBeerData.map(cat => ({
      ...cat,
      styles: cat.styles.filter(s => {
        const name = t(`beerGuide.styles.${s.key}.name`).toLowerCase();
        const origin = t(`beerGuide.styles.${s.key}.origin`).toLowerCase();
        const desc = t(`beerGuide.styles.${s.key}.desc`).toLowerCase();
        const search = searchTerm.toLowerCase();
        return name.includes(search) || origin.includes(search) || desc.includes(search);
      })
    })).filter(cat => cat.styles.length > 0);
  }, [getBeerData, searchTerm, t]);

  const getSrmColor = (srmRange) => {
    const srmValue = srmRange.includes('-') ? parseInt(srmRange.split('-')[0]) : parseInt(srmRange);
    if (srmValue <= 2) return '#FFE699';
    if (srmValue <= 4) return '#FFD878';
    if (srmValue <= 7) return '#EBA832';
    if (srmValue <= 12) return '#C37209';
    if (srmValue <= 18) return '#914304';
    if (srmValue <= 24) return '#5D2C04';
    if (srmValue <= 35) return '#241103';
    return '#080707';
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-slate-200 p-4 md:p-12 font-sans selection:bg-amber-500 selection:text-black">
      <div className="max-w-7xl mx-auto">
        
        {/* Header Section */}
        <header className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-white/10 pb-8">
          <div>
            <div className="flex items-center gap-3 text-amber-500 mb-2">
              {/* Beer Icon */}
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M7 8v10a2 2 0 002 2h6a2 2 0 002-2V8M9 8V6a2 2 0 012-2h2a2 2 0 012 2v2" />
              </svg>
              <span className="font-bold tracking-[0.3em] text-sm uppercase">{t('beerGuide.subtitle')}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter text-white uppercase">
              {t('beerGuide.title_part1')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">{t('beerGuide.title_part2')}</span>
            </h1>
          </div>
          
          <div className="flex flex-col items-end gap-4">
            <div className="text-right">
              <p className="text-slate-500 text-sm font-mono uppercase tracking-widest mb-1">{t('beerGuide.total_styles')}: 23</p>
              <p className="text-amber-500/80 text-[10px] italic">{t('beerGuide.updated')}</p>
            </div>
          </div>
        </header>

        {/* Search Bar */}
        <div className="mb-12">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-600 to-amber-400 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-500"></div>
            <div className="relative bg-[#1a1a1a] border border-white/10 rounded-2xl flex items-center px-6 py-1">
              <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input 
                type="text"
                placeholder={t('beerGuide.search_placeholder')}
                className="w-full bg-transparent py-5 pl-4 focus:outline-none text-lg text-white placeholder:text-slate-600"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Styles Content */}
        <div className="space-y-16">
          {filteredData.map((category, idx) => (
            <section key={idx}>
              <div className="flex items-center gap-4 mb-6 px-2">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10"></div>
                <h2 className="text-xs font-black uppercase tracking-[0.4em] text-amber-500 whitespace-nowrap">
                  {category.category}
                </h2>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10"></div>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                {category.styles.map((style, sIdx) => (
                  <div key={sIdx} className="bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 rounded-2xl p-6 lg:p-8 transition-all group lg:grid lg:grid-cols-12 lg:items-center gap-4">
                    <div className="col-span-3 mb-4 lg:mb-0">
                      <h3 className="text-xl font-bold text-white group-hover:text-amber-400 transition-colors leading-tight">
                        {t(`beerGuide.styles.${style.key}.name`)}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-slate-500 text-sm">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t(`beerGuide.styles.${style.key}.origin`)}
                      </div>
                    </div>

                    <div className="col-span-3 flex lg:grid lg:grid-cols-3 gap-6 mb-6 lg:mb-0 items-center justify-between md:justify-start">
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] text-slate-600 uppercase font-black lg:hidden">ABV</span>
                        <div className="flex items-center gap-1 text-amber-500/80 font-mono font-bold">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          {style.abv.split('-')[0]}%
                        </div>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] text-slate-600 uppercase font-black lg:hidden">IBU</span>
                        <div className="flex items-center gap-1 text-slate-300 font-mono">
                          <svg className="w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                          </svg>
                          {style.ibu}
                        </div>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] text-slate-600 uppercase font-black lg:hidden">Color</span>
                        <div 
                          className="w-10 h-3 rounded-full border border-white/10 my-1 shadow-lg" 
                          style={{ backgroundColor: getSrmColor(style.srm) }}
                        />
                        <span className="text-[10px] text-slate-500 font-mono">SRM {style.srm}</span>
                      </div>
                    </div>

                    <div className="col-span-6 border-t border-white/5 pt-4 lg:border-none lg:pt-0">
                      <p className="text-slate-400 text-sm leading-relaxed">
                        {t(`beerGuide.styles.${style.key}.desc`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Legend */}
        <section className="mt-24 bg-gradient-to-b from-amber-500/10 to-transparent border-t border-amber-500/20 p-8 md:p-12 rounded-t-[3rem]">
          <h3 className="text-2xl font-black text-white mb-8 flex items-center gap-3">
            <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('beerGuide.tech_legend')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-amber-500 font-bold uppercase tracking-widest text-xs">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                ABV
              </div>
              <p className="text-sm text-slate-400">{t('beerGuide.abv_desc')}</p>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-amber-500 font-bold uppercase tracking-widest text-xs">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                IBU
              </div>
              <p className="text-sm text-slate-400">{t('beerGuide.ibu_desc')}</p>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-amber-500 font-bold uppercase tracking-widest text-xs">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                SRM
              </div>
              <p className="text-sm text-slate-400">{t('beerGuide.srm_desc')}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BeerStyleGuide;